import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  RefreshCw, Play, Loader2, CheckCircle2, Globe, Eye, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const DWSyncSection = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [brandId, setBrandId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [previewing, setPreviewing] = useState({});
  const [previews, setPreviews] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/admin/dw-sync/products`, { headers });
      setProducts(data.products || []);
      setBrandId(data.brand_id);
    } catch (e) {
      console.error('Failed to fetch DW products', e);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handlePreview = async (key) => {
    setPreviewing(p => ({ ...p, [key]: true }));
    try {
      const { data } = await axios.post(`${API}/api/admin/dw-sync/preview/${key}`, {}, { headers });
      setPreviews(p => ({ ...p, [key]: data }));
      toast.success(`Found ${data.colors_found} colors on ${data.product}`);
    } catch (e) {
      toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
    } finally { setPreviewing(p => ({ ...p, [key]: false })); }
  };

  const handleSync = async (key) => {
    setSyncing(p => ({ ...p, [key]: true }));
    try {
      const { data } = await axios.post(`${API}/api/admin/dw-sync/run/${key}`, {}, { headers });
      toast.success(data.message);
      // Refresh after a delay to let background task finish
      setTimeout(() => { fetchProducts(); setSyncing(p => ({ ...p, [key]: false })); }, 5000);
    } catch (e) {
      toast.error(`Sync failed: ${e.response?.data?.detail || e.message}`);
      setSyncing(p => ({ ...p, [key]: false }));
    }
  };

  const handleSyncAll = async () => {
    setSyncing(p => ({ ...p, all: true }));
    try {
      const { data } = await axios.post(`${API}/api/admin/dw-sync/run-all`, {}, { headers });
      toast.success(data.message);
      setTimeout(() => { fetchProducts(); setSyncing(p => ({ ...p, all: false })); }, 10000);
    } catch (e) {
      toast.error(`Sync all failed: ${e.response?.data?.detail || e.message}`);
      setSyncing(p => ({ ...p, all: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dw-sync-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-neutral-800 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            DW Corp USA Sync
          </h3>
          <p className="text-sm text-neutral-500">
            Scrape product colors from{' '}
            <a href="https://www.dwcorpusa.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              dwcorpusa.com
            </a>
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={fetchProducts} className="gap-1.5">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleSyncAll}
            disabled={syncing.all || !brandId}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700"
            data-testid="dw-sync-all-btn"
          >
            {syncing.all ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Sync All Collections
          </Button>
        </div>
      </div>

      {!brandId && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          DW brand not found in database. Create a &quot;DW&quot; brand first in Fabric Collections.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {products.map(prod => (
          <div key={prod.key} className="border border-neutral-200 rounded-xl p-4 bg-white hover:border-blue-300 transition-colors" data-testid={`dw-product-${prod.key}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-neutral-900">{prod.name}</h4>
              {prod.synced_colors > 0 && (
                <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                  <CheckCircle2 className="w-3 h-3" />
                  {prod.synced_colors} colors
                </span>
              )}
            </div>
            <p className="text-xs text-neutral-500 mb-1">{prod.fabric_type}</p>
            <a href={prod.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mb-3 block truncate">
              {prod.url}
            </a>

            {/* Preview result */}
            {previews[prod.key] && (
              <div className="mb-3 p-2 bg-neutral-50 rounded-lg">
                <p className="text-xs text-neutral-600 mb-1">{previews[prod.key].colors_found} colors found</p>
                <div className="flex gap-1 flex-wrap">
                  {previews[prod.key].colors.slice(0, 8).map((c, i) => (
                    <div key={i} className="w-8 h-8 rounded border border-neutral-200 overflow-hidden" title={c.name}>
                      <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                  ))}
                  {previews[prod.key].colors.length > 8 && (
                    <span className="text-[10px] text-neutral-400 self-center">+{previews[prod.key].colors.length - 8}</span>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePreview(prod.key)}
                disabled={previewing[prod.key]}
                className="flex-1 gap-1 text-xs"
              >
                {previewing[prod.key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                Preview
              </Button>
              <Button
                size="sm"
                onClick={() => handleSync(prod.key)}
                disabled={syncing[prod.key] || !brandId}
                className="flex-1 gap-1 text-xs bg-blue-600 hover:bg-blue-700"
                data-testid={`dw-sync-${prod.key}-btn`}
              >
                {syncing[prod.key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                Sync
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DWSyncSection;
